// Page « Mes campagnes » — Rail A (owned/inbound).
// Liste des campagnes, création rapide, accès au détail (QR + lien).
import { useCallback, useEffect, useState } from "react";
import { Plus, Copy, QrCode, ExternalLink, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Loader, EmptyState, GhostButton, PrimaryButton,
  NAVY, CHAMPAGNE, IVORY, GOLD, SLATE, Metric,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

const DEFAULT_CONSENT = (
  "J'autorise le professionnel à me recontacter au sujet de ma demande, "
  + "conformément à la Loi 25 (Québec) et à la Loi canadienne anti-pourriel (LCAP). "
  + "Je peux retirer mon consentement à tout moment."
);

function publicUrlFor(slug) {
  const base = window.location.origin;
  return `${base}/c/${slug}`;
}

function copy(text, label = "Lien copié") {
  navigator.clipboard.writeText(text).then(() => toast.success(label));
}

function CreateCampaignForm({ onCreated, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", value_prop: "", cta_label: "Réserver mon diagnostic",
    consent_text: DEFAULT_CONSENT, ask_phone: true, ask_email: true,
    ask_city: true, extra_question: "",
    success_message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/leads-engine/campaigns", form);
      toast.success("Campagne créée.");
      onCreated?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="campaign-create-form"
         className="rounded-xl bg-white p-5 space-y-4"
         style={{ border: `1px solid ${CHAMPAGNE}55` }}>
      <h3 className="font-serif text-[18px]" style={{ color: NAVY }}>
        Nouvelle campagne
      </h3>
      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
              style={{ color: SLATE }}>Nom de la campagne</span>
        <input data-testid="campaign-name"
               value={form.name}
               onChange={(e) => set("name", e.target.value)}
               placeholder="Diagnostic protection financière gratuit"
               className="mt-1 w-full rounded-md p-2 text-[13px]"
               style={{ background: IVORY, border: `1px solid ${CHAMPAGNE}55`,
                        color: NAVY }} />
      </label>
      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
              style={{ color: SLATE }}>Proposition de valeur</span>
        <textarea data-testid="campaign-value-prop"
                  value={form.value_prop}
                  onChange={(e) => set("value_prop", e.target.value)}
                  rows={3}
                  placeholder="Recevez en 15 minutes un portrait clair de vos protections actuelles."
                  className="mt-1 w-full rounded-md p-2 text-[13px]"
                  style={{ background: IVORY, border: `1px solid ${CHAMPAGNE}55`,
                           color: NAVY }} />
      </label>
      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
              style={{ color: SLATE }}>Texte du bouton</span>
        <input data-testid="campaign-cta"
               value={form.cta_label}
               onChange={(e) => set("cta_label", e.target.value)}
               className="mt-1 w-full rounded-md p-2 text-[13px]"
               style={{ background: IVORY, border: `1px solid ${CHAMPAGNE}55`,
                        color: NAVY }} />
      </label>
      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
              style={{ color: SLATE }}>Question additionnelle (optionnelle)</span>
        <input data-testid="campaign-extra"
               value={form.extra_question}
               onChange={(e) => set("extra_question", e.target.value)}
               placeholder="Quel est votre objectif principal ?"
               className="mt-1 w-full rounded-md p-2 text-[13px]"
               style={{ background: IVORY, border: `1px solid ${CHAMPAGNE}55`,
                        color: NAVY }} />
      </label>
      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
              style={{ color: SLATE }}>Texte de consentement (Loi 25 · LCAP)</span>
        <textarea data-testid="campaign-consent"
                  value={form.consent_text}
                  onChange={(e) => set("consent_text", e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-md p-2 text-[13px]"
                  style={{ background: IVORY, border: `1px solid ${CHAMPAGNE}55`,
                           color: NAVY }} />
        <span className="text-[10px]" style={{ color: SLATE }}>
          Minimum 30 caractères. Sera affiché en clair sur la landing page.
        </span>
      </label>
      <div className="flex gap-2 justify-end">
        <GhostButton onClick={onCancel} testid="campaign-create-cancel">
          Annuler
        </GhostButton>
        <PrimaryButton onClick={submit} disabled={busy}
                       testid="campaign-create-submit">
          {busy ? "Création…" : "Créer la campagne"}
        </PrimaryButton>
      </div>
    </div>
  );
}

function CampaignCard({ campaign, onToggle, onOpen }) {
  const url = publicUrlFor(campaign.slug);
  const qr = "https://api.qrserver.com/v1/create-qr-code/?size=180x180"
                + "&data=" + encodeURIComponent(url);
  return (
    <article data-testid={`campaign-card-${campaign.campaign_id}`}
             className="rounded-xl bg-white p-5 flex flex-col md:flex-row gap-4 items-start"
             style={{ border: `1px solid ${CHAMPAGNE}44` }}>
      <img src={qr} alt="QR code"
           data-testid={`campaign-qr-${campaign.campaign_id}`}
           className="rounded-md"
           style={{ width: 120, height: 120,
                    border: `1px solid ${CHAMPAGNE}55`,
                    background: "white" }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-serif text-[17px]" style={{ color: NAVY }}>
            {campaign.name}
          </h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] px-2 py-0.5 rounded-full"
                style={{ background: campaign.active ? "#C7F0DC" : "#E5E7EB",
                         color: campaign.active ? "#166534" : "#334155" }}>
            {campaign.active ? "Active" : "Inactive"}
          </span>
        </div>
        <p className="mt-2 text-[13px]" style={{ color: SLATE }}>
          {campaign.value_prop}
        </p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <code data-testid={`campaign-url-${campaign.campaign_id}`}
                className="text-[11px] px-2 py-1 rounded"
                style={{ background: "#F5F5F0", color: NAVY }}>
            {url}
          </code>
          <button data-testid={`campaign-copy-${campaign.campaign_id}`}
                  onClick={() => copy(url)}
                  className="text-[11px] flex items-center gap-1"
                  style={{ color: NAVY }}>
            <Copy className="h-3 w-3" /> Copier
          </button>
          <a href={url} target="_blank" rel="noreferrer"
             data-testid={`campaign-open-${campaign.campaign_id}`}
             className="text-[11px] flex items-center gap-1"
             style={{ color: NAVY }}>
            <ExternalLink className="h-3 w-3" /> Ouvrir
          </a>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
          <Metric label="Soumissions"
                  value={campaign.submissions_count || 0}
                  testid={`campaign-subs-${campaign.campaign_id}`} />
          <Metric label="Leads créés"
                  value={campaign.leads_created_count || 0}
                  testid={`campaign-leads-${campaign.campaign_id}`} />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <button data-testid={`campaign-toggle-${campaign.campaign_id}`}
                onClick={() => onToggle?.(campaign)}
                className="text-[11px] flex items-center gap-1 px-2.5 py-1 rounded-full"
                style={{ border: `1px solid ${CHAMPAGNE}88`, color: NAVY }}>
          {campaign.active ? <ToggleRight className="h-3 w-3" />
                                    : <ToggleLeft className="h-3 w-3" />}
          {campaign.active ? "Désactiver" : "Réactiver"}
        </button>
        <button data-testid={`campaign-detail-${campaign.campaign_id}`}
                onClick={() => onOpen?.(campaign)}
                className="text-[11px] px-2.5 py-1 rounded-full"
                style={{ background: NAVY, color: IVORY }}>
          Voir les soumissions
        </button>
      </div>
    </article>
  );
}

function SubmissionsDrawer({ campaign, onClose }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(
          `/leads-engine/campaigns/${campaign.campaign_id}/submissions`);
        if (!cancel) setItems(r.data?.items || []);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [campaign.campaign_id]);
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="submissions-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full sm:w-[520px] h-full overflow-y-auto"
             style={{ background: IVORY }}>
        <header className="sticky top-0 px-6 py-4"
                style={{ background: NAVY, color: IVORY }}>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em]"
               style={{ color: GOLD }}>Soumissions</div>
          <h3 className="font-serif text-[16px]">{campaign.name}</h3>
        </header>
        <div className="p-6 space-y-3">
          {loading && <Loader label="Chargement…" />}
          {!loading && items.length === 0 && (
            <p className="text-[12px]" style={{ color: SLATE }}
               data-testid="submissions-empty">
              Aucune soumission pour l'instant. Partagez le lien ou le QR code.
            </p>
          )}
          {items.map((s) => (
            <div key={s.submission_id}
                 data-testid={`submission-row-${s.submission_id}`}
                 className="rounded-lg bg-white p-3"
                 style={{ border: `1px solid ${CHAMPAGNE}44` }}>
              <div className="font-serif text-[14px]" style={{ color: NAVY }}>
                {s.first_name} {s.last_name}
              </div>
              <div className="text-[11px] mt-1 space-x-2" style={{ color: SLATE }}>
                {s.email && <span>{s.email}</span>}
                {s.phone && <span>· {s.phone}</span>}
                {s.city && <span>· {s.city}</span>}
              </div>
              {s.extra_answer && (
                <p className="mt-2 text-[12px]" style={{ color: NAVY }}>
                  « {s.extra_answer} »
                </p>
              )}
              <div className="mt-2 text-[10px] font-mono"
                   style={{ color: SLATE }}>
                {s.created_at?.slice(0, 16).replace("T", " ")}
                {s.utm_source && ` · source: ${s.utm_source}`}
                {s.utm_campaign && ` · campagne: ${s.utm_campaign}`}
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

export default function AltioraCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads-engine/campaigns");
      setCampaigns(r.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || "Impossible de charger les campagnes.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggleActive = async (c) => {
    try {
      await api.patch(`/leads-engine/campaigns/${c.campaign_id}`,
                          { active: !c.active });
      toast.success(c.active ? "Campagne désactivée." : "Campagne réactivée.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec.");
    }
  };

  return (
    <PageShell testid="altiora-campaigns-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PageHeader
          eyebrow="ALTIORA Leads · Rail A"
          title="Mes campagnes d'acquisition"
          subtitle="Générez vos propres leads consentis (Loi 25 · LCAP) grâce à des landing pages, formulaires et QR codes partageables. Chaque soumission devient automatiquement un lead prioritaire dans ALTIORA."
          testid="altiora-campaigns-header"
          actions={
            <PrimaryButton onClick={() => setCreating((v) => !v)}
                           testid="campaigns-create-open">
              <Plus className="h-3 w-3 inline mr-1" />
              {creating ? "Fermer" : "Nouvelle campagne"}
            </PrimaryButton>
          } />

        {creating && (
          <div className="mt-6">
            <CreateCampaignForm
              onCreated={() => { setCreating(false); reload(); }}
              onCancel={() => setCreating(false)} />
          </div>
        )}

        <div className="mt-8 space-y-4" data-testid="campaigns-list">
          {loading && <Loader label="Chargement…" />}
          {!loading && campaigns.length === 0 && !creating && (
            <EmptyState icon={QrCode}
              title="Aucune campagne pour l'instant."
              description="Créez votre première campagne pour capturer des leads consentis directement dans ALTIORA."
              testid="campaigns-empty" />
          )}
          {campaigns.map((c) => (
            <CampaignCard key={c.campaign_id}
                          campaign={c}
                          onToggle={toggleActive}
                          onOpen={setOpenDetail} />
          ))}
        </div>
      </div>
      {openDetail && (
        <SubmissionsDrawer campaign={openDetail}
                           onClose={() => setOpenDetail(null)} />
      )}
    </PageShell>
  );
}
